import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PlaylistApi } from '@app/library/models/playlist-api.model';
import { Playlist } from '@app/library/models/playlist.model';
import {
  AddToPlaylistFailure,
  AddToPlaylistRequest,
  AddToPlaylistSuccess,
  CreatePlaylistManualFailure,
  CreatePlaylistManualRequest,
  CreatePlaylistManualSuccess,
} from '@app/library/states/playlists.actions';
import { PlaylistsState } from '@app/library/states/playlists.state';
import {
  PlaylistOfTrackRequest,
  PlaylistOfTrackSuccess,
} from '@app/library/states/tracks.actions';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { UiStoreHelperService } from '@radioking/shared/common-services';
import { SugestionInput } from '@radioking/ui/input';
import { InputColorPickerComponent } from '@radioking/ui/input-color-picker';
import { Observable, Subscription } from 'rxjs';

interface DataInput {
  trackIdList: number[];
}

@Component({
  selector: 'rk-add-to-playlist-modal',
  templateUrl: './add-to-playlist-modal.component.html',
  styleUrls: ['./add-to-playlist-modal.component.scss'],
})
export class AddToPlaylistModalComponent implements OnInit, OnDestroy {
  addToPlaylistForm: UntypedFormGroup;
  isLoading$: Observable<boolean>;

  @Select(PlaylistsState.manualPlaylistsToSuggestion)
  playlists$: Observable<SugestionInput[]>;

  @Select(PlaylistsState.firstManualPlaylist)
  firstPlaylist$: Observable<Playlist>;

  selectedPlaylist: SugestionInput;
  playlistContainingTrack: number[] = [];

  subscription = new Subscription();

  constructor(
    public readonly dialogRef: MatDialogRef<AddToPlaylistModalComponent>,
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly helper: UiStoreHelperService,
    @Inject(MAT_DIALOG_DATA) public dataIn: DataInput,
  ) {}

  ngOnInit() {
    this.isLoading$ = this.helper.getLoadingState(
      [AddToPlaylistRequest, CreatePlaylistManualRequest],
      [AddToPlaylistSuccess, AddToPlaylistFailure, CreatePlaylistManualFailure],
    );

    this.addToPlaylistForm = new UntypedFormBuilder().group({
      playlistUsed: 'new',
      selectedPlaylist: undefined,
      playlistName: ['', Validators.required],
    });
    this.subscription.add(
      this.firstPlaylist$.subscribe(playlist => {
        this.createForm(playlist);
      }),
    );
    this.subscription.add(
      this.actions$
        .pipe(ofActionDispatched(CreatePlaylistManualSuccess))
        .subscribe(() => this.dialogRef.close()),
    );
    this.subscription.add(
      this.actions$
        .pipe(ofActionDispatched(PlaylistOfTrackSuccess))
        .subscribe(({ idPlaylist }) => {
          this.playlistContainingTrack = idPlaylist;
        }),
    );
    if (this.dataIn.trackIdList.length === 1) {
      this.getPlaylistsOfTrack(this.dataIn.trackIdList[0]);
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  @Dispatch()
  getPlaylistsOfTrack(idTrack: number) {
    return new PlaylistOfTrackRequest(idTrack);
  }

  createForm(firstPlaylist: Playlist) {
    if (firstPlaylist) {
      this.addToPlaylistForm.get('playlistName').clearValidators();
      this.addToPlaylistForm.setValue({
        playlistUsed: 'existing',
        selectedPlaylist: firstPlaylist,
        playlistName: '',
      });
    }
    this.addToPlaylistForm.get('playlistUsed').valueChanges.subscribe(value => {
      const playlistName = this.addToPlaylistForm.get('playlistName');
      if (value === 'new') {
        playlistName.setValidators([Validators.required]);
      } else {
        playlistName.clearValidators();
      }
      playlistName.markAsDirty();
      playlistName.updateValueAndValidity();
    });
  }

  onType() {
    if (this.addToPlaylistForm.get('playlistUsed').value !== 'new') {
      this.addToPlaylistForm.get('playlistUsed').setValue('new');
    }
  }

  onSubmit() {
    const formValues = this.addToPlaylistForm.value;
    if (formValues.playlistUsed === 'existing') {
      this.dialogRef.close();
      this.store.dispatch(
        new AddToPlaylistRequest(formValues.selectedPlaylist.id, this.dataIn.trackIdList),
      );
    } else {
      const playlist: Partial<PlaylistApi> = {
        name: formValues.playlistName,
        color: InputColorPickerComponent.getRandomColor(),
        type: 'manual',
      };
      this.store.dispatch(
        new CreatePlaylistManualRequest(playlist, this.dataIn.trackIdList),
      );
    }
  }

  onAbort() {
    this.dialogRef.close(false);
  }
}
